import React, {
  InputHTMLAttributes,
  TextareaHTMLAttributes,
  useState,
} from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Button } from "@/components/ui/button";
import { X, ChevronDown, Search, Check } from "lucide-react";
import { cn } from "@/lib/utils";

interface StandardFormInputProps extends InputHTMLAttributes<HTMLInputElement> {
  label: string;
  error?: string;
  helperText?: string;
  required?: boolean;
}

interface StandardFormTextareaProps
  extends TextareaHTMLAttributes<HTMLTextAreaElement> {
  label: string;
  error?: string;
  helperText?: string;
  required?: boolean;
}

interface StandardFormSelectProps {
  label: string;
  value: string;
  onValueChange: (value: string) => void;
  options: { label: string; value: string }[];
  placeholder?: string;
  error?: string;
  helperText?: string;
  children?: React.ReactNode;
  disabled?: boolean;
  required?: boolean;
}

interface StandardFormTagInputProps {
  label: string;
  tags: string[];
  onTagsChange: (tags: string[]) => void;
  placeholder?: string;
  error?: string;
  helperText?: string;
  required?: boolean;
}

interface StandardFormMultiSelectProps {
  label: string;
  values: string[];
  onValuesChange: (values: string[]) => void;
  options: { label: string; value: string }[];
  placeholder?: string;
  error?: string;
  helperText?: string;
  children?: React.ReactNode;
  disabled?: boolean;
  required?: boolean;
}

export const StandardFormInput: React.FC<StandardFormInputProps> = ({
  label,
  error,
  helperText,
  className = "",
  required = false,
  ...props
}) => {
  return (
    <div className="space-y-1 standard-tag-input">
      <label className="block text-[13px] font-normal text-muted-foreground">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div className="bg-background border border-border rounded-xl overflow-hidden">
        <input
          {...props}
          className={`w-full px-4 py-2 h-[38px] bg-transparent placeholder-gray-500 outline-none ${className}`}
        />
      </div>
      {error && <p className="text-red-500 text-xs">{error}</p>}
      {helperText && (
        <p className="text-muted-foreground text-xs">{helperText}</p>
      )}
    </div>
  );
};

export const StandardFormTextarea: React.FC<StandardFormTextareaProps> = ({
  label,
  error,
  helperText,
  className = "",
  required = false,
  ...props
}) => {
  return (
    <div className="space-y-1">
      <label className="block text-[13px] font-normal text-muted-foreground">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div className="bg-background border border-border rounded-xl overflow-hidden">
        <textarea
          {...props}
          className={`w-full px-4 py-2 bg-transparent placeholder-gray-500 outline-none min-h-[80px] resize-vertical ${className}`}
        />
      </div>
      {error && <p className="text-red-500 text-xs">{error}</p>}
      {helperText && (
        <p className="text-muted-foreground text-xs">{helperText}</p>
      )}
    </div>
  );
};

export const StandardFormSelect: React.FC<StandardFormSelectProps> = ({
  label,
  value,
  onValueChange,
  options,
  placeholder = "Select option",
  error,
  helperText,
  children,
  disabled = false,
  required = false,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const selectedOption = options.find((option) => option.value === value);

  return (
    <div className="space-y-1">
      <label className="block text-[13px] font-normal text-muted-foreground">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div className="bg-background border border-border rounded-xl overflow-hidden">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              role="combobox"
              aria-expanded={open}
              disabled={disabled}
              className="w-full h-[38px] justify-between bg-transparent hover:bg-transparent px-4 py-2 font-normal"
            >
              <span
                className={cn(
                  "truncate text-left",
                  !selectedOption && "text-muted-foreground",
                )}
              >
                {selectedOption ? selectedOption.label : placeholder}
              </span>
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="p-0 bg-secondary-background text-foreground border border-border rounded-b-xl shadow-[0_10px_15px_-3px_rgba(0,0,0,0.4),0_4px_6px_-2px_rgba(0,0,0,0.25)] dark:shadow-[0_10px_15px_-3px_rgba(0,0,0,0.8),0_4px_6px_-2px_rgba(0,0,0,0.6)]"
            align="start"
            style={{ width: "var(--radix-popover-trigger-width)" }}
          >
            <div className="flex items-center border-b border-border px-3 bg-secondary-background">
              <Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
              <input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex h-10 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground text-foreground disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
            <div className="max-h-[300px] overflow-y-auto overflow-x-hidden bg-secondary-background rounded-b-xl">
              {children && <div className="p-1">{children}</div>}
              <div className="p-1">
                {filteredOptions.length === 0 ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    No options found.
                  </div>
                ) : (
                  filteredOptions.map((option) => (
                    <div
                      key={option.value}
                      className={cn(
                        "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
                        value === option.value &&
                          "bg-accent text-accent-foreground",
                      )}
                      onClick={() => {
                        onValueChange(option.value);
                        setOpen(false);
                        setSearchQuery("");
                      }}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4 text-primary",
                          value === option.value ? "opacity-100" : "opacity-0",
                        )}
                      />
                      <span className="truncate">{option.label}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      {error && <p className="text-red-500 text-xs">{error}</p>}
      {helperText && (
        <p className="text-muted-foreground text-xs">{helperText}</p>
      )}
    </div>
  );
};

export const StandardFormTagInput: React.FC<StandardFormTagInputProps> = ({
  label,
  tags,
  onTagsChange,
  placeholder = "Enter tags, press Enter to add",
  error,
  helperText,
  required = false,
}) => {
  const [inputValue, setInputValue] = useState("");

  const handleInputChange = (value: string) => {
    setInputValue(value);
  };

  const addTag = () => {
    const trimmedValue = inputValue.trim();
    if (trimmedValue && !tags.includes(trimmedValue)) {
      onTagsChange([...tags, trimmedValue]);
      setInputValue("");
    }
  };

  const handleRemoveTag = (indexToRemove: number) => {
    onTagsChange(tags.filter((_, index) => index !== indexToRemove));
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addTag();
    } else if (e.key === "Backspace" && inputValue === "" && tags.length > 0) {
      e.preventDefault();
      onTagsChange(tags.slice(0, -1));
    }
  };

  return (
    <div className="space-y-1">
      <label className="block text-[13px] font-normal text-muted-foreground">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div className="bg-background border border-border rounded-xl overflow-hidden">
        <div className="flex items-center gap-2 px-2 py-1 flex-wrap min-h-[38px]">
          {tags.map((tag, idx) => (
            <div
              key={idx}
              className="bg-primary/30 text-white rounded-full px-2 flex items-center gap-1 h-6 text-xs"
            >
              <span className="line-clamp-1">
                {tag.length > 15 ? tag.slice(0, 15) + "..." : tag}
              </span>
              <button
                type="button"
                onClick={() => handleRemoveTag(idx)}
                className="cursor-pointer hover:text-red-300"
              >
                <X className="w-3 h-3" />
              </button>
            </div>
          ))}
          <input
            value={inputValue}
            type="text"
            placeholder={placeholder}
            onChange={(e) => handleInputChange(e.target.value)}
            onKeyDown={handleKeyDown}
            className="flex-1 min-w-[120px] bg-transparent outline-none placeholder-gray-500 text-sm py-1"
          />
        </div>
      </div>
      {error && <p className="text-red-500 text-xs">{error}</p>}
      {helperText && (
        <p className="text-muted-foreground text-xs">{helperText}</p>
      )}
    </div>
  );
};

export const StandardFormMultiSelect: React.FC<StandardFormMultiSelectProps> = ({
  label,
  values,
  onValuesChange,
  options,
  placeholder = "Select options",
  error,
  helperText,
  children,
  disabled = false,
  required = false,
}) => {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredOptions = options.filter((option) =>
    option.label.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  const selectedOptions = options.filter((option) =>
    values.includes(option.value),
  );

  const toggleOption = (optionValue: string) => {
    if (values.includes(optionValue)) {
      onValuesChange(values.filter((v) => v !== optionValue));
    } else {
      onValuesChange([...values, optionValue]);
    }
  };

  const removeOption = (optionValue: string) => {
    onValuesChange(values.filter((v) => v !== optionValue));
  };

  return (
    <div className="space-y-1">
      <label className="block text-[13px] font-normal text-muted-foreground">
        {label}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
      <div className="bg-background border border-border rounded-xl overflow-hidden">
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              role="combobox"
              aria-expanded={open}
              disabled={disabled}
              className="w-full min-h-[38px] h-auto justify-between bg-transparent hover:bg-transparent px-4 py-2 font-normal"
            >
              <div className="flex flex-wrap gap-1 flex-1">
                {selectedOptions.length > 0 ? (
                  selectedOptions.map((option) => (
                    <div
                      key={option.value}
                      className="bg-primary/30 text-white rounded-full px-2 flex items-center gap-1 h-6 text-xs"
                      onClick={(e) => {
                        e.stopPropagation();
                        removeOption(option.value);
                      }}
                    >
                      <span className="line-clamp-1 max-w-[200px]">
                        {option.label}
                      </span>
                      <X className="w-3 h-3 cursor-pointer hover:text-red-300" />
                    </div>
                  ))
                ) : (
                  <span className="text-muted-foreground">{placeholder}</span>
                )}
              </div>
              <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="p-0 bg-secondary-background text-foreground border border-border rounded-b-xl shadow-[0_10px_15px_-3px_rgba(0,0,0,0.4),0_4px_6px_-2px_rgba(0,0,0,0.25)] dark:shadow-[0_10px_15px_-3px_rgba(0,0,0,0.8),0_4px_6px_-2px_rgba(0,0,0,0.6)]"
            align="start"
            style={{ width: "var(--radix-popover-trigger-width)" }}
          >
            <div className="flex items-center border-b border-border px-3 bg-secondary-background">
              <Search className="mr-2 h-4 w-4 shrink-0 text-muted-foreground" />
              <input
                placeholder="Search..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="flex h-10 w-full bg-transparent py-3 text-sm outline-none placeholder:text-muted-foreground text-foreground disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
            <div className="max-h-[300px] overflow-y-auto overflow-x-hidden bg-secondary-background rounded-b-xl">
              {children && <div className="p-1">{children}</div>}
              <div className="p-1">
                {filteredOptions.length === 0 ? (
                  <div className="py-6 text-center text-sm text-muted-foreground">
                    No options found.
                  </div>
                ) : (
                  filteredOptions.map((option) => (
                    <div
                      key={option.value}
                      className={cn(
                        "relative flex cursor-pointer select-none items-center rounded-sm px-2 py-1.5 text-sm outline-none transition-colors hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground",
                        values.includes(option.value) &&
                          "bg-accent text-accent-foreground",
                      )}
                      onClick={() => toggleOption(option.value)}
                    >
                      <Check
                        className={cn(
                          "mr-2 h-4 w-4 text-primary",
                          values.includes(option.value)
                            ? "opacity-100"
                            : "opacity-0",
                        )}
                      />
                      <span className="truncate">{option.label}</span>
                    </div>
                  ))
                )}
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>
      {error && <p className="text-red-500 text-xs">{error}</p>}
      {helperText && (
        <p className="text-muted-foreground text-xs">{helperText}</p>
      )}
    </div>
  );
};
